// @ts-check
import { defineStore } from "pinia";
import UsersApi, { type UserResp } from "@/data/api-layer/usersApi";
import { useAuthStore } from "./authStore";
import { useAccountStore } from "./accountStore";
import { useFlowParticipationStore } from "./flowParticipationStore";

const useUserStore = defineStore("userStore", () => {
   const user = ref<UserResp>(null);
   const authStore = useAuthStore();
   const accountStore = useAccountStore();
   const flowParticipationStore = useFlowParticipationStore();

   // is the user account being represented by someone else like carer/parent/legal gardian etc?
   const isAccountRepresented = ref(false);

   // is an Admin/HCP impersonating another user
   const isImpersonating = ref(false);

   // [Assuming the core profile portal setting is enabled] The core profile flow appears after a user has set their password
   // This variable is used to determine whether the user has completed the core profile flow which is required to access the portal
   const isCoreProfileFlowComplete = ref(null);

   async function setUser(): Promise<UserResp> {
      user.value = await UsersApi.get();
      return user.value;
   }

   function $reset() {
      user.value = null;
   }

   function clearAllUserData() {
      // Called from routes-config i) on a public route ii) there is no auth token (logged out) iii) there is still user data
      $reset();
      accountStore.$reset();
      flowParticipationStore.$reset();
   }

   async function login(email, password, queryParams, surveyResponse, privacyServices) {
      const response = await UsersApi.login({
         email,
         password: password,
         query_parameters: queryParams,
         survey_response: surveyResponse,
         privacy_services: privacyServices ? { data: privacyServices } : {},
         legacy_hash: false, // Use the new hashing method
      });
      return response;
   }

   async function logout() {
      const response = await UsersApi.logout();
      authStore.destroyToken();
      return response;
   }

   async function updateCommsPreference(communicationType, isEnabled) {
      if (communicationType === "marketing") {
         if (isEnabled) {
            const response = await UsersApi.updateCommsPreferencePatch();
            return response;
         }
         // disabling marketing setting
         else {
            const response = await UsersApi.updateCommsPreferenceDel();
            return response;
         }
      }
   }

   return {
      login,
      logout,
      setUser,
      updateCommsPreference,
      clearAllUserData,
      user,
      isAccountRepresented,
      isImpersonating,
      isCoreProfileFlowComplete,
   };
});

export { useUserStore };
